import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import {
  ANNOUNCEMENT,
  ANNOUNCEMENT_RECEIPT_SUMMARY,
  ANNOUNCEMENT_RECEIPTS,
} from '../../graphql/announcement-queries'
import LoadingSpinner from '../../components/LoadingSpinner'
import { formatRelativeTime } from '../../components/announcements/announcementFormat'
import type {
  AnnouncementReceiptRow,
  AnnouncementReceiptSummary,
} from '../../types/announcement'

type FilterKey = 'all' | 'unread' | 'unacked'

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Not read' },
  { key: 'unacked', label: 'Not acknowledged' },
]

const AnnouncementReceiptsPage = () => {
  const { id } = useParams<{ id: string }>()
  const announcementId = id ? parseInt(id, 10) : null
  const [filter, setFilter] = useState<FilterKey>('all')

  const { data: annData } = useQuery(ANNOUNCEMENT, {
    variables: { id: announcementId },
    skip: announcementId == null,
  })
  const { data: summaryData } = useQuery(ANNOUNCEMENT_RECEIPT_SUMMARY, {
    variables: { announcement_id: announcementId },
    skip: announcementId == null,
  })
  const { data: receiptsData, loading } = useQuery(ANNOUNCEMENT_RECEIPTS, {
    variables: { announcement_id: announcementId, filter: filter === 'all' ? undefined : filter },
    skip: announcementId == null,
    fetchPolicy: 'cache-and-network',
  })

  const announcement = annData?.announcement
  const summary: AnnouncementReceiptSummary | undefined = summaryData?.announcementReceiptSummary
  const rows: AnnouncementReceiptRow[] = receiptsData?.announcementReceipts ?? []

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link to="/admin/announcements" className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {announcement?.title ?? 'Receipts'}
          </h1>
          <p className="mt-1 text-sm text-gray-500">Who has read and acknowledged this announcement.</p>
        </div>
      </div>

      {/* Summary + filter */}
      <div className="card p-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-6 text-sm">
          <span className="text-gray-700">
            Read <strong>{summary?.read ?? 0}</strong>/{summary?.total ?? 0}
          </span>
          <span className="text-gray-700">
            Acknowledged <strong>{summary?.acknowledged ?? 0}</strong>/{summary?.total ?? 0}
          </span>
        </div>
        <div className="flex rounded-lg overflow-hidden ring-1 ring-gray-200">
          {FILTERS.map((f, i) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                filter === f.key ? 'bg-primary-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
              } ${i > 0 ? 'border-l border-gray-200' : ''}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Recipient list */}
      {loading && rows.length === 0 ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : rows.length === 0 ? (
        <div className="card py-12 text-center text-sm text-gray-400">No recipients match this filter.</div>
      ) : (
        <div className="card divide-y divide-gray-100">
          {rows.map((r) => (
            <div key={r.recipient.id} className="flex items-center justify-between gap-4 px-4 py-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{r.recipient.name}</p>
                <p className="text-xs text-gray-400 truncate">{r.recipient.email}</p>
              </div>
              <div className="flex items-center gap-4 text-xs flex-shrink-0">
                {r.read_at ? (
                  <span className="text-gray-600">read {formatRelativeTime(r.read_at)}</span>
                ) : (
                  <span className="text-gray-400">— not read</span>
                )}
                {r.acknowledged_at ? (
                  <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
                    <CheckCircleIcon className="h-4 w-4" />
                    acked
                  </span>
                ) : (
                  <span className="text-amber-600">— not acked</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default AnnouncementReceiptsPage
